import { Box, Flex, Grid, Heading, List, ListItem, Text, useToken } from '@chakra-ui/react';

import { Card } from '~/components/card';
import { Carousel } from '~/components/carousel';
import { ContentContainer } from '~/components/container';
import { HeadingWithEmphasis } from '~/components/heading-with-emphasis';
import { QuoteIcon } from '~/components/quote-icon';
import { SanityImage } from '~/components/sanity-image';
import { Testimonial } from '~/features/testimonials/models';

export type TestimonialsSectionProps = {
  title?: string | null;
  testimonials: Array<{
    context: string;
    testimonial: Testimonial;
  }>;
  layout?: 'carousel' | 'grid';
};

export function TestimonialsSection({ title, testimonials, layout = 'grid' }: TestimonialsSectionProps) {
  const [blue500, seafoam600, seafoam700] = useToken('colors', [
    'blue.500',
    'accent.sea-foam.600',
    'accent.sea-foam.700',
  ]);

  if (layout === 'carousel') {
    return (
      <Box
        as='section'
        padding={{
          base: '40px 0px',
          md: '80px 0px',
        }}
      >
        <Carousel
          scrollBarColorOverride={seafoam600}
          theme='light'
          containerProps={{
            sx: {
              '--carousel-heading-offset-top': '122px',
              '--carousel-scrollbar-offset-top': '44px',
              '--carousel-offset-start': {
                base: '16px',
                md: '24px',
                lg: '48px',
                xl: 'unset',
              },
              '--carousel-slide-display': 'flex',
            },
          }}
          items={testimonials.map(({ context, testimonial }, idx) => (
            <Card
              key={idx}
              as='article'
              display='flex'
              flexDir='column'
              flexShrink={0}
              width={{
                base: '370px',
                xl: '832px',
              }}
            >
              <Flex
                alignItems='center'
                bgColor='#D4F9F2'
                py={4}
                px={{
                  base: 4,
                  md: 8,
                }}
              >
                <Heading
                  as='p'
                  color='accent.sea-foam.700'
                  fontWeight='400'
                  size={{
                    base: 'marketing-md',
                    xl: 'marketing-lg',
                  }}
                >
                  {context}
                </Heading>
              </Flex>
              <Flex
                flex='1'
                flexDir='column'
                gap={6}
                p={{
                  base: 4,
                  md: 8,
                }}
              >
                <Flex alignItems='center' gap={12}>
                  <Flex as='blockquote' flexDir='column' gap={6}>
                    <QuoteIcon color={seafoam700} />
                    <HeadingWithEmphasis
                      text={testimonial.quote}
                      headingProps={{
                        as: 'p',
                        size: {
                          base: 'marketing-md',
                          md: 'marketing-lg',
                        },
                        fontWeight: '400',
                        sx: {
                          '&::after': {
                            content: '"\\201D"',
                          },
                        },
                      }}
                    />
                  </Flex>
                  {testimonial.profileImage && (
                    <Box
                      borderRadius='4px 13px'
                      flexShrink={0}
                      height='136px'
                      overflow='hidden'
                      position='relative'
                      width='136px'
                      display={{
                        base: 'none',
                        xl: 'block',
                      }}
                    >
                      <SanityImage
                        alt=''
                        image={testimonial.profileImage}
                        sizes='136px'
                        style={{
                          objectFit: 'cover',
                          pointerEvents: 'none',
                        }}
                        fill
                      />
                    </Box>
                  )}
                </Flex>

                <Flex alignItems='center' gap={4} marginTop='auto'>
                  {testimonial.profileImage && (
                    <Box
                      borderRadius='2px 6px'
                      flexShrink={0}
                      height={{ base: '48px', md: '64px' }}
                      overflow='hidden'
                      position='relative'
                      width={{ base: '48px', md: '64px' }}
                      display={{
                        base: 'block',
                        xl: 'none',
                      }}
                    >
                      <SanityImage alt='' image={testimonial.profileImage} sizes='128px' fill />
                    </Box>
                  )}
                  <Box>
                    <Heading
                      as='p'
                      color='accent.sea-foam.700'
                      fontFamily='body'
                      fontWeight='500'
                      size={{
                        base: 'marketing-md',
                        md: 'marketing-lg',
                      }}
                    >
                      {testimonial.name}
                    </Heading>
                    <Text
                      fontSize={{
                        base: 'xs',
                        md: 'md',
                      }}
                    >
                      {testimonial.title}
                    </Text>
                  </Box>
                  {testimonial?.company?.hifiLogo && (
                    <Box marginLeft='auto'>
                      <SanityImage alt='' image={testimonial.company.hifiLogo} />
                    </Box>
                  )}
                </Flex>
              </Flex>
            </Card>
          ))}
          navButtonProps={{
            colorScheme: 'sea-foam',
          }}
          titleSlot={
            <Box
              mb='16px'
              paddingEnd={{
                base: '16px',
                md: 'unset',
              }}
              paddingStart={{
                base: '16px',
                md: '24px',
                lg: '48px',
                xl: 'unset',
              }}
            >
              <Heading as='h2' size='marketing-3xl' whiteSpace='pre-wrap'>
                {title}
              </Heading>
            </Box>
          }
        />
      </Box>
    );
  }

  return (
    <Box
      as='section'
      padding={{
        base: '40px 0px',
        md: '80px 0px',
      }}
    >
      <ContentContainer>
        {Boolean(title) && (
          <Heading
            as='h2'
            textAlign='center'
            mb={{
              base: 8,
              md: 10,
            }}
            size={{
              base: 'marketing-3xl',
              xl: 'marketing-5xl',
            }}
            whiteSpace={{
              xl: 'pre-wrap',
            }}
          >
            {title}
          </Heading>
        )}
        <List
          as={Grid}
          gap={8}
          gridTemplateColumns={{
            md: '1fr 1fr',
          }}
        >
          {testimonials.map(({ testimonial, context }, idx) => (
            <ListItem key={idx} display='flex'>
              <Card as='article' display='flex' flexDir='column'>
                <Flex
                  alignItems='center'
                  bgColor='blue.100'
                  py={4}
                  px={{
                    base: 4,
                    md: 8,
                  }}
                >
                  <Heading
                    as='p'
                    color='blue.500'
                    fontWeight='400'
                    size={{
                      base: 'marketing-md',
                      md: 'marketing-lg',
                    }}
                  >
                    {context}
                  </Heading>
                </Flex>
                <Flex
                  flex='1'
                  flexDir='column'
                  gap={6}
                  p={{
                    base: 4,
                    md: 8,
                  }}
                >
                  <Flex as='blockquote' flexDir='column' gap={6}>
                    <QuoteIcon color={blue500} />
                    <HeadingWithEmphasis
                      text={testimonial.quote}
                      headingProps={{
                        as: 'p',
                        size: {
                          base: 'marketing-md',
                          md: 'marketing-lg',
                        },
                        fontWeight: '400',
                        sx: {
                          '&::after': {
                            content: '"\\201D"',
                          },
                        },
                      }}
                    />
                  </Flex>
                  <Flex alignItems='center' gap={4} marginTop='auto'>
                    {testimonial.profileImage && (
                      <Box
                        borderRadius='2px 6px'
                        flexShrink={0}
                        height={{ base: '48px', md: '64px' }}
                        overflow='hidden'
                        position='relative'
                        width={{ base: '48px', md: '64px' }}
                      >
                        <SanityImage alt='' image={testimonial.profileImage} sizes='128px' fill />
                      </Box>
                    )}
                    <Box>
                      <Heading
                        as='p'
                        color='blue.500'
                        fontFamily='body'
                        fontWeight='500'
                        size={{
                          base: 'marketing-md',
                          md: 'marketing-lg',
                        }}
                      >
                        {testimonial.name}
                      </Heading>
                      <Text
                        fontSize={{
                          base: 'xs',
                          md: 'md',
                        }}
                      >
                        {testimonial.title}
                      </Text>
                    </Box>
                    {testimonial?.company?.hifiLogo && (
                      <Box marginLeft='auto'>
                        <SanityImage alt='' image={testimonial.company.hifiLogo} />
                      </Box>
                    )}
                  </Flex>
                </Flex>
              </Card>
            </ListItem>
          ))}
        </List>
      </ContentContainer>
    </Box>
  );
}
